import {
  doc,
  getDoc,
  runTransaction,
  updateDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore"
import { db } from "../../../lib/firebase"
import { generateRoomCode, shuffle, PLAYER_COLORS } from "../../../utils/scattergoriesLogic"
import { JUST_ONE_WORDS } from "../../../data/justOneWords"

const ID_KEY = "justone_player_id"
const NAME_KEY = "justone_player_name"

export const roomRef = (code) => doc(db, "justone_rooms", code.toUpperCase())

export function getIdentity() {
  let playerId = localStorage.getItem(ID_KEY)
  if (!playerId) {
    playerId = Math.random().toString(36).slice(2, 10)
    localStorage.setItem(ID_KEY, playerId)
  }
  return { playerId, name: localStorage.getItem(NAME_KEY) || "" }
}

export function saveIdentity(name) {
  localStorage.setItem(NAME_KEY, name)
}

const twelveHours = () => new Date(Date.now() + 12 * 60 * 60 * 1000)

function playerEntry(name, color) {
  return { name, color, score: 0, joinedAt: Date.now() }
}

export async function createRoom(hostName) {
  const { playerId } = getIdentity()
  saveIdentity(hostName)
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = generateRoomCode()
    const created = await runTransaction(db, async (tx) => {
      const snap = await tx.get(roomRef(code))
      if (snap.exists()) return false
      tx.set(roomRef(code), {
        phase: "lobby",
        round: 0,
        totalRounds: 7,
        duration: 90, // seconds
        hostId: playerId,
        createdAt: serverTimestamp(),
        expireAt: twelveHours(),
        players: { [playerId]: playerEntry(hostName, PLAYER_COLORS[0]) },
        usedWords: [],
        clues: {},
        eliminatedClues: {},
        guess: null,
        guessStatus: null,
        lastResult: null,
        nextRoom: null,
        history: [],
        teamScore: 0,
      })
      return true
    })
    if (created) return code
  }
  throw new Error("could-not-create")
}

export async function joinRoom(code, name) {
  const { playerId } = getIdentity()
  saveIdentity(name)
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists()) throw new Error("not-found")
    const players = snap.data().players || {}
    if (players[playerId]) {
      tx.update(roomRef(code), { [`players.${playerId}.name`]: name })
      return
    }
    const color = PLAYER_COLORS[Object.keys(players).length % PLAYER_COLORS.length]
    tx.update(roomRef(code), {
      [`players.${playerId}`]: playerEntry(name, color),
    })
  })
}

export async function updateRoomSettings(code, settings) {
  await updateDoc(roomRef(code), settings)
}

export async function startGame(code, { totalRounds, duration }) {
  const chosenWord = shuffle(JUST_ONE_WORDS)[0]

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "lobby") return
    const room = snap.data()
    const playerIds = Object.keys(room.players || {})
    
    // Choose host or first player as first guesser
    const guesserId = playerIds.includes(room.hostId) ? room.hostId : playerIds[0]

    tx.update(roomRef(code), {
      phase: "clue",
      round: 1,
      totalRounds,
      duration,
      guesserId,
      secretWord: chosenWord,
      usedWords: [chosenWord],
      clues: {},
      eliminatedClues: {},
      guess: null,
      guessStatus: null,
      lastResult: null,
      history: [],
      teamScore: 0,
      // Reset all player scores to 0 when starting a fresh game
      ...Object.keys(room.players || {}).reduce((acc, pid) => {
        acc[`players.${pid}.score`] = 0
        return acc
      }, {}),
    })
  })
}

export function cleanWord(w) {
  return (w || "").trim().toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")
}

export async function submitClue(code, round, playerId, clue) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists()) return

    const room = snap.data()
    if (room.round !== round || room.phase !== "clue") return

    const currentClues = { ...(room.clues || {}), [playerId]: clue }
    const playerIds = Object.keys(room.players || {})
    const guesserId = room.guesserId

    // Filter clue givers (everyone except the guesser)
    const clueGivers = playerIds.filter((pid) => pid !== guesserId)
    const allSubmitted = clueGivers.every((pid) => !!currentClues[pid])

    if (allSubmitted) {
      // Perform auto duplicate detection!
      const secret = cleanWord(room.secretWord)
      const eliminated = {}

      // Count clean clue frequencies
      const frequencies = {}
      clueGivers.forEach((pid) => {
        const val = cleanWord(currentClues[pid])
        if (val) {
          frequencies[val] = (frequencies[val] || 0) + 1
        }
      })

      clueGivers.forEach((pid) => {
        const raw = currentClues[pid]
        const val = cleanWord(raw)

        // 1. Matches secret word
        if (val === secret || val.includes(secret) || secret.includes(val)) {
          eliminated[pid] = true
        }
        // 2. Exact match with another player's clue
        else if (frequencies[val] > 1) {
          eliminated[pid] = true
        }
      })

      tx.update(roomRef(code), {
        clues: currentClues,
        eliminatedClues: eliminated,
        phase: "review",
      })
    } else {
      tx.update(roomRef(code), {
        [`clues.${playerId}`]: clue,
      })
    }
  })
}

export async function toggleEliminatedClue(code, targetPid, isEliminated) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "review") return
    tx.update(roomRef(code), {
      [`eliminatedClues.${targetPid}`]: isEliminated,
    })
  })
}

export async function endReview(code) {
  await updateDoc(roomRef(code), {
    phase: "guess",
    guessEndAt: Date.now() + 90 * 1000, // 90s default to guess
  })
}

export async function submitGuess(code, round, guess, isPass = false) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists()) return

    const room = snap.data()
    if (room.round !== round || room.phase !== "guess") return

    const secret = cleanWord(room.secretWord)
    const cleanedGuess = cleanWord(guess)
    const isCorrect = !isPass && cleanedGuess === secret

    const guessStatus = isPass ? "passed" : isCorrect ? "correct" : "incorrect"
    
    // Tabulate points
    const scoresGained = {}
    const players = { ...room.players }
    const guesserId = room.guesserId
    const clueGivers = Object.keys(players).filter((pid) => pid !== guesserId)

    // Guesser gets +2 on correct
    scoresGained[guesserId] = isCorrect ? 2 : 0
    players[guesserId].score += scoresGained[guesserId]

    // Clue givers get +1 if their clue was unique AND the guesser guessed correctly
    clueGivers.forEach((pid) => {
      const clueUnique = !room.eliminatedClues?.[pid]
      scoresGained[pid] = (isCorrect && clueUnique) ? 1 : 0
      players[pid].score += scoresGained[pid]
    })

    const newTeamScore = room.teamScore + (isCorrect ? 1 : 0)

    const roundResult = {
      round: room.round,
      secretWord: room.secretWord,
      guesserId,
      guesserName: players[guesserId]?.name || "Unknown",
      clues: room.clues || {},
      eliminatedClues: room.eliminatedClues || {},
      guess: isPass ? "PASSED" : guess,
      guessStatus,
      scoresGained,
    }

    const history = [...(room.history || []), roundResult]

    tx.update(roomRef(code), {
      phase: "reveal",
      players,
      teamScore: newTeamScore,
      lastResult: roundResult,
      history,
    })
  })
}

export async function nextRound(code) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "reveal") return
    const room = snap.data()

    if (room.round >= room.totalRounds) {
      tx.update(roomRef(code), { phase: "gameover" })
      return
    }

    const playerIds = Object.keys(room.players || {})
    
    // Select next guesser by joinedAt order
    const sortedPids = [...playerIds].sort((a, b) => {
      return (room.players[a].joinedAt || 0) - (room.players[b].joinedAt || 0)
    })
    const curIdx = sortedPids.indexOf(room.guesserId)
    const nextGuesserId = sortedPids[(curIdx + 1) % sortedPids.length]

    // Pick new secret word
    const unusedWords = JUST_ONE_WORDS.filter((w) => !(room.usedWords || []).includes(w))
    const chosenWord = unusedWords.length ? shuffle(unusedWords)[0] : shuffle(JUST_ONE_WORDS)[0]

    tx.update(roomRef(code), {
      phase: "clue",
      round: room.round + 1,
      guesserId: nextGuesserId,
      secretWord: chosenWord,
      usedWords: [...(room.usedWords || []), chosenWord],
      clues: {},
      eliminatedClues: {},
      guess: null,
      guessStatus: null,
    })
  })
}

export async function endGame(code) {
  await updateDoc(roomRef(code), { phase: "gameover" })
}

export async function playAgain(code, hostName) {
  const { playerId: hostId } = getIdentity()
  const oldSnap = await getDoc(roomRef(code))
  const oldPlayers = oldSnap.exists() ? oldSnap.data().players || {} : {}

  const newCode = await createRoom(hostName)

  const carried = {}
  Object.entries(oldPlayers).forEach(([pid, player]) => {
    if (pid === hostId) return
    carried[`players.${pid}`] = {
      name: player.name,
      color: player.color,
      joinedAt: player.joinedAt,
      score: 0,
    }
  })

  if (Object.keys(carried).length > 0) {
    await updateDoc(roomRef(newCode), carried)
  }
  await updateDoc(roomRef(code), { nextRoom: newCode })
  return newCode
}

export function subscribeRoom(code, onData, onError) {
  return onSnapshot(
    roomRef(code),
    (snap) => onData(snap.exists() ? snap.data() : null),
    onError
  )
}
